import type { SelectorStrategy } from '@chatsdet/types';

/**
 * Build a Playwright locator string from a selector strategy.
 *
 * Returns the locator string in priority order (testId, role, label, placeholder, text, css).
 * Used during replay to find elements and during recording to pre-render the locator each
 * captured event would produce.
 *
 * @param selectors - Selector strategy from a cached action or captured recording event.
 * @returns Playwright locator chain as a string, e.g. `getByRole('button', { name: 'Sign in' })`.
 */
export function buildLocatorString(selectors: SelectorStrategy): string {
  const parts: string[] = [];

  if (selectors.testId) {
    parts.push(`getByTestId(${JSON.stringify(selectors.testId)})`);
  }

  if (selectors.role) {
    const { role, name, exact } = selectors.role;
    if (name) {
      const options = exact
        ? `{ name: ${JSON.stringify(name)}, exact: true }`
        : `{ name: ${JSON.stringify(name)} }`;
      parts.push(`getByRole('${role}', ${options})`);
    } else {
      parts.push(`getByRole('${role}')`);
    }
  }

  if (selectors.ariaLabel) {
    parts.push(`getByLabel(${JSON.stringify(selectors.ariaLabel)})`);
  }

  if (selectors.placeholder) {
    parts.push(`getByPlaceholder(${JSON.stringify(selectors.placeholder)})`);
  }

  if (selectors.text) {
    const { text, exact } = selectors.text;
    if (exact) {
      parts.push(`getByText(${JSON.stringify(text)}, { exact: true })`);
    } else {
      parts.push(`getByText(${JSON.stringify(text)})`);
    }
  }

  if (selectors.css) {
    parts.push(`locator(${JSON.stringify(selectors.css)})`);
  }

  return parts.length > 0 ? parts[0] : 'locator("*")';
}
